import { Component, h, Prop } from '@stencil/core';
import { coveoConfig } from '../../global/coveo-components';
import Util from '../../global/util/util';
import { CoveoSearchEngineService } from '../../service/coveo-engine.service';
import { CoveoTokenService } from '../../service/coveo-token.service';
import { default as languageStrings } from '../../global/l10n/CoveoSearchBox.json';

@Component({
	tag: 'ti-coveo-search-box',
	styleUrl: 'coveo-search-box.scss',
	shadow: false,
})
export class CoveoSearchBox {

	// domain of the page
	private _domain: string = Util.getDomain();

	private _lang: string = 'en';

	// Atomic Search Interface
	private _searchInterface: any;

	/**
	 * locale of the page
	 */
	@Prop() locale: string = coveoConfig.locale;

	/**
	 *  Which search page to redirect to after a search
	 */
	@Prop() redirectUrl: string = coveoConfig.redirectUrl;

	/**
	 * Id to use for your search box
	 */
	@Prop() searchBoxId: string = coveoConfig.defaultSearchBoxId

	/**
	 * Id to use for your search Interface
	 */
	@Prop() searchInterfaceId: string = coveoConfig.defaultSearchInterfaceId;

	/**
	 * Whether or not to show recently searched queries in the search dropdown
	 */
	@Prop() showRecentQueries: boolean = true;

	/**
	 * Whether or not to suggest potential search queries in the searchbox dropdown
	 */
	@Prop() showSearchSuggestions: boolean = true;

	/**
	 * default false, make true if looking to update
	 * to new atomic 3.0 search box look
	 */
	@Prop() textArea: boolean = false;

	async componentWillLoad() {
		// Check the provided locale is valid, Util will retrieve the
		// correct locale
		await Util.getLangPref(this.locale).then(resovledLocale => {
			this.locale = resovledLocale;
			this._lang = resovledLocale.includes('zh') ? resovledLocale : resovledLocale.substring(0, 2);
		});

		await CoveoTokenService.getCoveoSearchToken(this._domain)
			.then(coveoToken => {
				if (null == coveoToken || null == coveoToken.orgId || null == coveoToken.token) {
					coveoToken = {
						orgId: coveoToken.orgId ?? coveoConfig.orgId,
						token: coveoToken.token ?? coveoConfig.accessToken
					};
				}
				CoveoSearchEngineService.setUpEngine(coveoToken);
			});
	}

	async componentDidLoad() {
		await this._searchInterface?.initializeWithSearchEngine(
			CoveoSearchEngineService.getSearchEngine()
		);

		this._searchInterface?.i18n?.addResourceBundle(this._lang, 'translation', {
			'clear': languageStrings[this.locale]['clear']
		});

		// add metrics for search icon
		let searchIcon: HTMLElement;
		let querySuggestBox: HTMLElement;
		// We only want to track header searchbox metrics at this time.
		if (this.searchBoxId === coveoConfig.defaultSearchBoxId) {
			await Util.waitForTrue(() => {
				searchIcon = document.getElementById(coveoConfig.defaultSearchBoxId)
					?.shadowRoot?.querySelector('button[part="submit-button"]');
				querySuggestBox = document.getElementById(coveoConfig.defaultSearchBoxId)
					?.shadowRoot.querySelector(`${this.textArea ? 'atomic-focus-detector textarea' : 'atomic-focus-detector input'}`)
				return searchIcon && querySuggestBox;
			}).catch(() => {
				return Promise.resolve();
			});
			searchIcon?.setAttribute('data-navtitle', 'search icon');
			querySuggestBox.setAttribute('data-navtitle', 'search suggestion');

			searchIcon.addEventListener('click', () => {
				Util.makeAnalyticsCall('onsite search interaction', 'search icon click', 'search bar|search icon', 0);
			});

			['keydown', 'focus'].forEach(evnt => {
				querySuggestBox.addEventListener(evnt, (e: any) => {
					if (evnt == 'keydown') {
						if (e.key == 'Enter') {
							Util.makeAnalyticsCall('onsite search interaction', 'return button press', 'search bar|return button', 0);
						}
					}
					document.getElementById(coveoConfig.defaultSearchBoxId)
						?.shadowRoot.querySelectorAll('atomic-focus-detector div[id*="popup"] button[id*="suggestion-qs"]').forEach(suggestion => {
							suggestion.removeEventListener('click', this._querySuggestClick);
							suggestion.addEventListener('click', this._querySuggestClick, { once: true });
						});
					document.getElementById(coveoConfig.defaultSearchBoxId)
						?.shadowRoot.querySelectorAll('atomic-focus-detector div[id*="popup"] button[id*="suggestion-recent"]').forEach(suggestion => {
							suggestion.removeEventListener('click', this._queryRecentClick);
							suggestion.addEventListener('click', this._queryRecentClick, { once: true });
						});
				});
			});
		}
	}

	private _queryRecentClick() {
		Util.makeAnalyticsCall('onsite search interaction', 'query recent click', 'search bar|recent click', 0);
	}

	private _querySuggestClick() {
		Util.makeAnalyticsCall('onsite search interaction', 'query suggestion click', 'search bar|suggestion click', 0);
	}

	render() {
		return (
			<atomic-search-interface
				ref={(el) => this._searchInterface = el}
				id={this.searchInterfaceId}
				language={this._lang}>
				<atomic-search-box
					redirection-url={this.redirectUrl
						.replace('{0}', this._domain)
						.replace('{1}', this.locale.toLowerCase())
						.replace('{2}', this.locale)
					}
					textarea={this.textArea}
					id={this.searchBoxId}
					minimum-query-length="2">
					{this.showSearchSuggestions &&
						<atomic-search-box-query-suggestions max-with-query="5" max-without-query="3" />
					}
					{this.showRecentQueries &&
						<atomic-search-box-recent-queries max-with-query="5" max-without-query="3" />
					}
				</atomic-search-box>
			</atomic-search-interface>
		);
	}
}
